import React, { useCallback, useEffect, useState } from 'react';
import { useMachine } from '@xstate/react';
import { createFarkleGame, gameContext, gameEvent } from '../../game/Farkle';
import FarkleThreeCanvas from '../../three/ThreeCanvas';
import { mergeBoolean } from '../../game/Turn';
import GameButtons from '../GameControls';
import TurnStatus from '../TurnStatus';
import ScoreTable from '../ScoreTable';
import { State, Interpreter } from 'xstate';
import { Redirect } from 'react-router-dom';
import FarkleBotComponent from '../../game/bots/FarkleBotComponent';
import { Player } from '../../game/player';
import './Game.styles.scss';
import { useMessage } from '../../MessageContext';
import Message from '../Message';

// Auto end-turn after x milliseconds
const FARKLE_DELAY = 2000;

type InternalGameComponentProps = {
  current: State<gameContext, gameEvent>,
  send: Interpreter<gameContext, any, gameEvent>['send'],
  endGame?():any,
  host: "local" | "remote",
  player?: string,
  bots?: Array<number>
}
const InternalGameComponent = ({current, send, host, bots, player, endGame}: InternalGameComponentProps) => {
  const { message, showMessage, sendMessage, clearMessage } = useMessage()
  const turnState = Object.values(current.value)[0]
  const [isPlayersTurn, setIsPlayersTurn] = useState(false)
  const [isCurrentPlayerBot, setIsCurrentPlayerBot] = useState(false)

  const logState = () => {console.log(current)}
  // const debug = () => {
  //   logState()
  //   debugger
  // }

  const sendGameEvent = useCallback(send, [])
  const _sendMessage = useCallback(sendMessage, [])
  const _clearMessage = useCallback(clearMessage, [])

  useEffect(() => {
    const getWinner = () => (current.context.winner !== null) ? current.context.players[current.context.winner] : null
    const isFarkle = (): boolean => Object.values(current.value)[0] === 'farkle'
    const isRolling = (): boolean => Object.values(current.value)[0] === 'rolling'
    if (current.matches('idle')) {
      sendGameEvent('START')
    } else if (current.matches('end_game') && getWinner()) {
      _sendMessage(`${getWinner()?.name} wins!`)
      console.log('Game over', current.context)
    } else if (isFarkle()){
      _sendMessage('Farkle')
      setTimeout(() => {sendGameEvent('END_TURN')}, FARKLE_DELAY);
    } else if (!isPlayersTurn && isRolling()) {
      _sendMessage('Rolling')
    } else {
      _clearMessage()
      const currentPlayer = current.context.players[current.context.player]
      const isBot = !!bots && bots?.includes(current.context.player)
      setIsCurrentPlayerBot(isBot)
      setIsPlayersTurn((host === "remote" && currentPlayer.name === player) || (host === "local" && !isBot))
    }
  }, [current, bots, player, host, isPlayersTurn, sendGameEvent, _sendMessage, _clearMessage])

  return (
    <div className="game">
      <Message show={showMessage}>{message}</Message>

      <FarkleThreeCanvas
        gameState={current}
        frozenDice={mergeBoolean(
          current.context.frozen,
          current.context.frozenThisRoll
        )}
        sendGameEvent={sendGameEvent}
        isPlayersTurn={isPlayersTurn || isCurrentPlayerBot}
      />
      <ScoreTable
        names={current.context.players.map(p => p.name)}
        scores={current.context.scores}
        currentPlayer={current.context.player}
        onlinePlayer={player}
        turnState={turnState}
      />
      <div className="game-chrome">
        <TurnStatus
          turnState={turnState}
          playerId={current.context.player}
          turnScore={current.context.scoreThisRoll}
        />
        <GameButtons 
          turnState={turnState}
          sendGameEvent={sendGameEvent}
          endGame={endGame}
          context={current.context}
          isPlayersTurn={isPlayersTurn}
          isGameOver={current.matches('end_game')}
        />
      </div>
      {
        process.env.NODE_ENV === 'development' && 
        <button className="log-button" onClick={() => logState()}>Log</button>
      }

      <FarkleBotComponent 
        current={current} 
        send={sendGameEvent} 
        bots={bots}
      />
    </div>
  )  
}

type LocalGameProps = {
  players: Array<Player>,
}
export const LocalGame = ({ players }: LocalGameProps) => {
  try {
  // Try creating a machine 
  const [current, send] = useMachine(createFarkleGame(players));
  const bots = players
    .map((p, i) => { return { t: p.type, i } })
    .filter(ti => ti.t === 'computer')
    .map(ti => ti.i)
  return <InternalGameComponent current={current} send={send} bots={bots} host="local"/>
  } catch (error) {
    // go back home if we can't create the machine
    return <Redirect to="/" />
  }
}

// TODO => `current` is being sent as gameContext
// server needs to sent the current state
type RemoteGameProps = {
  current: any,//State<gameContext, gameEvent, any, any>
  player: string, 
  send(type: string, payload?: any): any,
  close():any
}
export const RemoteGame = ({current, send, player, close}: RemoteGameProps) => {
  const sentEvent = send as Interpreter<gameContext, any, gameEvent>['send']
  return <InternalGameComponent 
    current={current} 
    send={sentEvent} 
    endGame={close}
    player={player} 
    host="remote"
  />
}
